import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface TencentImageCreation3Params {
  /**
   * 文本描述，算法将根据输入的文本智能生成与之相关的图像。不能为空，推荐使用中文。最多可传1024个 utf-8 字符。
   * @length 0 - 1024
   * @example "一只可爱的橘猫在阳光下睡觉，照片风格，高清画质"
   */
  prompt: string;
  /**
   * 生成图分辨率，支持以下尺寸：640:1408, 704:1344, 768:1280, 832:1216, 896:1152, 960:1088, 1024:1024, 1088:960, 1152:896, 1216:832, 1280:768, 1344:704, 1408:640
   * @default "1024:1024"
   * @values 640:1408 | 704:1344 | 768:1280 | 832:1216 | 896:1152 | 960:1088 | 1024:1024 | 1088:960 | 1152:896 | 1216:832 | 1280:768 | 1344:704 | 1408:640
   * @example "1024:1024"
   */
  resolution?: '640:1408' | '704:1344' | '768:1280' | '832:1216' | '896:1152' | '960:1088' | '1024:1024' | '1088:960' | '1152:896' | '1216:832' | '1280:768' | '1344:704' | '1408:640';
  /**
   * 随机种子，默认随机。不传：随机种子生成。正数：固定种子生成。注意：改写开启时固定种子不生效，将保持随机。
   * @example 12345
   */
  seed?: number;
  /**
   * 为生成结果图添加显式水印标识的开关，默认为1。1：添加。0：不添加。建议使用显著标识来提示结果图使用了 AI 绘画技术，是 AI 生成的图片。
   * @default 1
   * @values 0 | 1
   * @example 1
   */
  logo_add?: 0 | 1;
  /**
   * 标识内容设置。默认在生成结果图右下角添加'图片由 AI 生成'字样，您可根据自身需要替换为其他的标识图片。
   */
  logo_param?: {
  };
  /**
   * 是否开启prompt改写，默认开启。1：开启（改写预计会增加20s左右耗时）。0：关闭（如果关闭改写，需要调用方自己优化prompt，否则对生图效果有较大影响）。
   * @default 1
   * @values 0 | 1
   */
  revise?: 0 | 1;
}

export interface TencentImageCreation3Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * tencent_image_creation_3
 *
 * <p>使用腾讯智能创作服务生成图片，支持多种分辨率和自定义水印</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, tencentImageCreation3 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await tencentImageCreation3({
 *   // 参数见 TencentImageCreation3Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 TencentImageCreation3Params 接口定义
 * @returns 生成的资源列表
 */
export async function tencentImageCreation3(
  params: TencentImageCreation3Params,
): Promise<TencentImageCreation3Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'tencent_image_creation_3',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: TencentImageCreation3Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
